/*
 * Broadcast Theme
 *
 * Use this file to add custom Javascript to Broadcast.  Keeping your custom
 * Javascript in this fill will make it easier to update Broadcast. In order
 * to use this file you will need to open layout/theme.liquid and uncomment
 * the custom.js script import line near the bottom of the file.
 */

class RingAngleViewer extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
  }

  connectedCallback() {
    this.image = this.querySelector(
      ".ring-angle-viewer__image:not(.ring-angle-viewer__image--placeholder)",
    );
    // One row of view buttons per metal; views come from product images (snippets/ring-angle-viewer.liquid)
    this.rows = [...this.querySelectorAll("[data-angle-metal]")];
    this.activeRow = this.rows.find((row) => !row.hidden) || null;
    this.miniButton = this.querySelector("[data-angle-mini]");
    this.miniImage = this.miniButton?.querySelector("img");
    this.variantMetals = this.parseVariantMetals();
    this.loadToken = 0;
    this.observers = [];

    this.addEventListener("click", this.onClick);

    if (window.subscribe && window.theme?.PUB_SUB_EVENTS) {
      this.unsubscribeVariantChange = window.subscribe(
        window.theme.PUB_SUB_EVENTS.variantChange,
        this.onVariantChange,
      );
    }

    if (this.image) {
      this.initMiniImage();
      this.preloadViews();
    }
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClick);
    this.unsubscribeVariantChange?.();
    this.observers.forEach((observer) => observer.disconnect());
    this.mobileQuery?.removeEventListener("change", this.onMobileChange);
    clearTimeout(this.preloadTimer);
  }

  parseVariantMetals() {
    try {
      return JSON.parse(
        this.querySelector("[data-angle-metals]")?.textContent || "{}",
      );
    } catch (error) {
      console.warn("ring-angle-viewer: invalid variant data", error);
      return {};
    }
  }

  get buttons() {
    return this.activeRow
      ? [...this.activeRow.querySelectorAll("[data-angle-view]")]
      : [];
  }

  get activeButton() {
    return this.buttons.find((button) =>
      button.classList.contains("is-active"),
    );
  }

  onClick(event) {
    if (event.target.closest("[data-angle-mini]")) {
      this.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    const button = event.target.closest("[data-angle-view]");
    if (!button || button.hidden || button === this.activeButton) return;

    this.setImage(button.dataset);
    this.setActive(button);
  }

  onVariantChange({ data } = {}) {
    const variantId = data?.variant?.id;
    if (!variantId) return;

    const metal = this.variantMetals[variantId];
    const row = this.rows.find((item) => item.dataset.angleMetal === metal);
    if (!row || row === this.activeRow) return; // same metal (e.g. only the ring size changed): keep the view

    const currentViewName = this.activeButton?.dataset.angleView;
    this.rows.forEach((item) => {
      item.hidden = item !== row;
    });
    this.activeRow = row;

    // Keep the same view (e.g. "Quarter View") if this metal has it, otherwise its first view
    const next =
      this.buttons.find((button) => button.dataset.angleView === currentViewName) ||
      this.buttons[0];

    if (!next) {
      // Metal without labelled images: fall back to the product's main image
      if (this.dataset.fallbackSrc) this.setImage({ src: this.dataset.fallbackSrc });
      return;
    }

    this.setImage(next.dataset);
    this.setActive(next);
  }

  setActive(activeButton) {
    this.buttons.forEach((button) => {
      const isActive = button === activeButton;
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-pressed", String(isActive));
    });
  }

  setImage(view) {
    if (!view || !this.image || view.src === this.image.getAttribute("src"))
      return;

    const token = ++this.loadToken;
    const next = this.image.cloneNode();

    next.removeAttribute("fetchpriority");
    next.classList.add("is-entering");
    next.srcset = view.srcset || "";
    next.src = view.src;
    next.alt = view.alt || "";

    next
      .decode()
      .catch(() => {})
      .then(() => {
        if (token !== this.loadToken || !this.image) return;

        const previous = this.image;
        previous.after(next);
        this.image = next;

        next.getBoundingClientRect();
        next.classList.remove("is-entering");
        previous.classList.add("is-leaving");

        const duration =
          parseFloat(getComputedStyle(previous).transitionDuration) * 1000 || 0;
        setTimeout(() => previous.remove(), duration);

        if (this.miniImage) {
          this.miniImage.removeAttribute("srcset");
          this.miniImage.src = view.src;
        }
      });
  }

  preloadViews() {
    const views = [...this.querySelectorAll("[data-angle-view]")].map(
      (button) => button.dataset,
    );

    const unique = [...new Map(views.map((view) => [view.src, view])).values()];
    const sizes = this.image.getAttribute("sizes") || "";

    const load = (index) => {
      if (index >= unique.length || !this.isConnected) return;

      const image = new Image();
      image.onload = image.onerror = () => load(index + 1);
      image.sizes = sizes;
      if (unique[index].srcset) image.srcset = unique[index].srcset;
      image.src = unique[index].src;
    };

    this.preloadTimer = setTimeout(() => load(0), 500);
  }

  initMiniImage() {
    if (!this.miniButton) return;

    let imageOut = false;
    let beforeButton = true;

    this.mobileQuery = window.matchMedia("(max-width: 749px)");

    const update = () => {
      this.miniButton.classList.toggle(
        "is-visible",
        imageOut && beforeButton && this.mobileQuery.matches,
      );
    };

    this.onMobileChange = update;
    this.mobileQuery.addEventListener("change", this.onMobileChange);

    const imageObserver = new IntersectionObserver(([entry]) => {
      imageOut = !entry.isIntersecting;
      update();
    });
    imageObserver.observe(this.querySelector(".ring-angle-viewer__stage"));
    this.observers.push(imageObserver);

    const addToCart =
      this.closest("product-info")?.querySelector("[data-add-to-cart]");
    if (addToCart) {
      const buttonObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            beforeButton = false;
          } else if (entry.boundingClientRect.top > 0) {
            beforeButton = true;
          }
          update();
        },
        { rootMargin: "0px 0px -80% 0px" },
      );
      buttonObserver.observe(addToCart);
      this.observers.push(buttonObserver);
    }
  }
}

if (!customElements.get("ring-angle-viewer")) {
  customElements.define("ring-angle-viewer", RingAngleViewer);
}

class StoneVisualizer extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onDialogClick = this.onDialogClick.bind(this);
    this.onDialogClose = this.onDialogClose.bind(this);
    this.onDialogCancel = this.onDialogCancel.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
  }

  connectedCallback() {
    this.dialog = this.querySelector("[data-stone-dialog]");
    this.hand = this.querySelector("[data-stone-hand]");
    this.shapeName = this.querySelector("[data-stone-shape-name]");
    this.shapeButtons = [...this.querySelectorAll("button[data-stone-shape]")];
    this.stoneImages = [...this.querySelectorAll("img[data-stone-shape]")];
    this.caratButtons = [...this.querySelectorAll("[data-stone-carat]")];
    this.minCarat = Math.min(
      ...this.caratButtons.map((button) => parseFloat(button.dataset.stoneCarat)),
    );

    this.addEventListener("click", this.onClick);
    this.dialog?.addEventListener("click", this.onDialogClick);
    this.dialog?.addEventListener("close", this.onDialogClose);
    this.dialog?.addEventListener("cancel", this.onDialogCancel);

    if (window.subscribe && window.theme?.PUB_SUB_EVENTS) {
      this.unsubscribeVariantChange = window.subscribe(
        window.theme.PUB_SUB_EVENTS.variantChange,
        this.onVariantChange,
      );
    }

    this.setCarat(this.dataset.carat);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClick);
    this.dialog?.removeEventListener("click", this.onDialogClick);
    this.dialog?.removeEventListener("close", this.onDialogClose);
    this.dialog?.removeEventListener("cancel", this.onDialogCancel);
    this.unsubscribeVariantChange?.();
    if (this.dialog?.open) this.dialog.close();
  }

  onClick(event) {
    if (event.target.closest("[data-stone-open]")) {
      this.open();
      return;
    }

    if (event.target.closest("[data-stone-close]")) {
      this.close();
      return;
    }

    const shapeButton = event.target.closest("button[data-stone-shape]");
    if (shapeButton) {
      this.setShape(shapeButton);
      return;
    }

    const caratButton = event.target.closest("[data-stone-carat]");
    if (caratButton) this.setCarat(caratButton.dataset.stoneCarat);
  }

  onDialogClick(event) {
    if (event.target === this.dialog) this.close();
  }

  onDialogClose() {
    this.dialog.classList.remove("is-closing");
    window.dispatchEvent(new CustomEvent("theme:scroll:unlock"));
  }

  onDialogCancel(event) {
    event.preventDefault();
    this.close();
  }

  onVariantChange({ data } = {}) {
    const variant = data?.variant;
    const position = parseInt(this.dataset.caratOptionPosition, 10);
    if (!variant || !position || data.sectionId !== this.dataset.sectionId)
      return;

    const carat = variant.options?.[position - 1];
    if (this.caratButtons.some((button) => button.dataset.stoneCarat === carat)) {
      this.setCarat(carat);
    }
  }

  open() {
    if (!this.dialog) return;

    if (this.dialog.classList.contains("is-closing")) {
      this.dialog.classList.remove("is-closing");
      return;
    }

    if (this.dialog.open) return;

    this.dialog.showModal();
    window.dispatchEvent(
      new CustomEvent("theme:scroll:lock", { detail: this.dialog }),
    );
  }

  close() {
    if (!this.dialog?.open || this.dialog.classList.contains("is-closing"))
      return;

    this.dialog.classList.add("is-closing");

    if (getComputedStyle(this.dialog).animationName === "none") {
      this.dialog.close();
      return;
    }

    this.dialog.addEventListener(
      "animationend",
      (event) => {
        if (
          event.target === this.dialog &&
          this.dialog.classList.contains("is-closing")
        ) {
          this.dialog.close();
        }
      },
      { once: true },
    );
  }

  setShape(activeButton) {
    const shape = activeButton.dataset.stoneShape;

    this.shapeButtons.forEach((button) => {
      const isActive = button === activeButton;
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-pressed", String(isActive));
    });

    this.stoneImages.forEach((image) => {
      image.classList.toggle("is-active", image.dataset.stoneShape === shape);
    });

    if (this.shapeName) this.shapeName.textContent = activeButton.dataset.stoneName;
  }

  setCarat(carat) {
    const activeButton = this.caratButtons.find(
      (button) => button.dataset.stoneCarat === carat,
    );
    if (!activeButton) return;

    this.caratButtons.forEach((button) => {
      const isActive = button === activeButton;
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-pressed", String(isActive));
    });

    const scale = Math.cbrt(parseFloat(carat) / this.minCarat) || 1;
    this.hand?.style.setProperty("--carat-scale", scale.toFixed(3));
  }
}

if (!customElements.get("stone-visualizer")) {
  customElements.define("stone-visualizer", StoneVisualizer);
}

(function () {
  // Add custom code below this line
  // ^^ Keep your scripts inside this IIFE function call to
  // avoid leaking your variables into the global scope.
})();
